import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Label } from '@/shared/ui/label';

interface Participant {
  id: string | number;
  name: string;
  email: string;
  phone?: string;
  gender: string;
  ageGroup?: string;
  status: string;
  joinDate?: string;
  source?: string;
  sourceDetail?: string;
  notes?: string;
  customFields?: Array<{name: string, value: string}>;
  [key: string]: any; // Allow additional properties
}

interface ChangeStatusDialogProps {
  participant: Participant | null;
  isOpen: boolean;
  onClose: () => void;
  onStatusChange: (participantId: string | number, newStatus: string) => void;
}

const statusOptions = [
  { value: 'Not Scheduled', label: 'Not Scheduled' },
  { value: 'Scheduled', label: 'Scheduled' },
  { value: 'Complete', label: 'Complete' },
  { value: 'Dropout', label: 'Dropout' },
];

export default function ChangeStatusDialog({
  participant,
  isOpen,
  onClose,
  onStatusChange,
}: Readonly<ChangeStatusDialogProps>) {
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  React.useEffect(() => {
    if (participant) {
      setSelectedStatus(participant.status);
    }
  }, [participant]);

  const handleStatusUpdate = () => {
    if (participant && selectedStatus) {
      onStatusChange(participant.id, selectedStatus);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedStatus(participant?.status || '');
    onClose();
  };

  if (!participant) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Change Participant Status</DialogTitle>
          <DialogDescription>
            Update the project status for {participant.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium">New Status</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={handleStatusUpdate}>
              Update Status
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

